import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";
import { fetchRideDetails } from "@/services/rideDetailsService";
import { RideDetailsView } from "@/components/rideInfo/RideDetailsView";
import { DetailsLoader } from "@/components/common/DetailsLoader";

const RIDE_ID_PATTERN = /^H[A-Za-z0-9]{9}$/;
const RIDE_ID_ERROR =
  "Ride ID must start with H and be exactly 10 characters long.";

export default function RideInfo() {
  const [searchParams, setSearchParams] = useSearchParams();
  // The URL is the source of truth: changing `rideId` (via the form or directly in the
  // address bar) drives the query below and triggers a fresh fetch automatically.
  const activeRideId = (searchParams.get("rideId") ?? "").trim();
  const [rideId, setRideId] = useState(activeRideId);
  const [validationError, setValidationError] = useState<string | null>(null);

  const normalizedRideId = rideId.trim().toUpperCase();
  const isRideIdValid = RIDE_ID_PATTERN.test(normalizedRideId);

  useEffect(() => {
    setRideId(activeRideId);
    setValidationError(null);
  }, [activeRideId]);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Ride Info — Duri Ridesharing";
    return () => {
      document.title = prevTitle;
    };
  }, []);

  const {
    data: ride,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["ride-details", activeRideId],
    queryFn: () => fetchRideDetails({ rideId: activeRideId, context: "host" }),
    enabled: activeRideId.length > 0,
    retry: false,
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!normalizedRideId) return;
    if (!isRideIdValid) {
      setValidationError(RIDE_ID_ERROR);
      return;
    }
    setValidationError(null);
    setSearchParams({ rideId: normalizedRideId });
  };

  const clearSearch = () => setSearchParams({});

  return (
    <div className="min-h-[80vh] bg-muted/30 px-4 py-12 sm:py-16">
      <div className="mx-auto max-w-2xl">
        {activeRideId && isLoading ? (
          <DetailsLoader
            title="Fetching ride details"
            subtitle="Hang tight while we pull up everything about this ride."
          />
        ) : activeRideId && isError ? (
          <div className="space-y-4">
            <button
              type="button"
              onClick={clearSearch}
              className="text-sm font-semibold text-emerald-700 hover:underline"
            >
              ← Search another ride
            </button>
            <div className="flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {(error as Error)?.message ||
                  "We couldn't find that ride. Please check the Ride ID and try again."}
              </span>
            </div>
          </div>
        ) : ride ? (
          <>
            <button
              type="button"
              onClick={clearSearch}
              className="mb-4 text-sm font-semibold text-emerald-700 hover:underline"
            >
              ← Search another ride
            </button>
            <RideDetailsView ride={ride} />
          </>
        ) : (
          <>
            <h1 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              Find your ride
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Enter your Ride ID to see full details.
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-3">
              <input
                type="text"
                value={rideId}
                onChange={(event) => {
                  setRideId(event.target.value.toUpperCase());
                  if (validationError) setValidationError(null);
                }}
                placeholder="e.g. H253OXIW2E"
                aria-label="Ride ID"
                maxLength={10}
                className="h-12 w-full rounded-xl border-2 border-emerald-500 bg-card px-4 text-sm uppercase outline-none transition-shadow focus:ring-2 focus:ring-emerald-200"
              />
              {validationError && (
                <p className="text-sm text-destructive">{validationError}</p>
              )}
              <button
                type="submit"
                disabled={!normalizedRideId}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 text-sm font-bold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Get Ride Details
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
